import { registerAuditLabels } from "@vixio/ui";
import type { StatusMap } from "@vixio/ui";

/** Tipos das linhas e RPCs do módulo cases (ver modules/cases/README.md). */

export type CaseStatus = "draft" | "in_review" | "approved" | "published" | "withdrawn";
export type Sector = "saude" | "educacao" | "empresa" | "publico";

export const CASE_STATUS: StatusMap = {
  draft: { label: "rascunho", tone: "neutral" },
  in_review: { label: "em revisão", tone: "info" },
  approved: { label: "aprovado", tone: "ok" },
  published: { label: "publicado", tone: "ok" },
  withdrawn: { label: "retirado", tone: "warn" },
};

export const SECTOR_LABEL: Record<Sector, string> = { saude: "Saúde", educacao: "Educação", empresa: "Empresa", publico: "Setor público" };

export interface CaseView {
  id: string;
  org_id: string;
  slug: string;
  title: string;
  sector: Sector;
  client_label: string;
  status: CaseStatus;
  anonymized: boolean;
  submitted_at: string | null;
  approved_at: string | null;
  published_at: string | null;
  withdrawn_at: string | null;
  withdrawn_reason: string | null;
  missing_fields: string[];
  authorization_id: string | null;
  has_active_authorization: boolean;
  allows_org_name: boolean | null;
  allows_numbers: boolean | null;
  allows_testimonial: boolean | null;
  testimonials_total: number;
  testimonials_approved: number;
  created_at: string;
  updated_at: string;
}

export interface CaseRow {
  id: string;
  org_id: string;
  slug: string;
  title: string;
  sector: Sector;
  client_label: string;
  client_name: string | null;
  client_org_ref: string | null;
  context: string | null;
  problem: string | null;
  intervention: string | null;
  baseline_text: string | null;
  result_text: string | null;
  denominator_text: string | null;
  limitations: string | null;
  not_improved: string | null;
  validator_name: string | null;
  validator_role: string | null;
  baseline_value: number | null;
  result_value: number | null;
  value_unit: string | null;
  denominator_n: number | null;
  anonymized: boolean;
  measurement_study_ref: string | null;
  status: CaseStatus;
}

export interface CaseAuthorization {
  id: string;
  case_id: string;
  granted_by_name: string;
  granted_by_role: string;
  scope: string;
  allows_org_name: boolean;
  allows_numbers: boolean;
  allows_testimonial: boolean;
  document_ref: string | null;
  granted_at: string;
  revoked_at: string | null;
  revoked_reason: string | null;
}

export interface Testimonial {
  id: string;
  case_id: string;
  text: string;
  author_role: string;
  author_name: string | null;
  approved_by_client_at: string | null;
  approved_evidence: string | null;
  created_at: string;
}

/** Campos de texto da ficha, na ordem e com os rótulos de cases.missing_fields. */
export const TEXT_FIELDS: Array<{ key: keyof CaseRow; label: string; hint?: string }> = [
  { key: "context", label: "Contexto" },
  { key: "problem", label: "Problema" },
  { key: "intervention", label: "O que foi feito" },
  { key: "baseline_text", label: "Linha de base", hint: "Como era antes, medido." },
  { key: "result_text", label: "Resultado medido" },
  { key: "denominator_text", label: "Denominador", hint: "Sobre quantos casos, em que período." },
  { key: "limitations", label: "Limitações" },
  { key: "not_improved", label: "O que não melhorou" },
];

declare module "@vixio/data" {
  interface RpcRegistry {
    "cases.upsert_case": {
      args: {
        p_org: string;
        p_slug: string;
        p_title: string;
        p_sector: Sector;
        p_client_label: string;
        p_context?: string | null;
        p_problem?: string | null;
        p_intervention?: string | null;
        p_baseline_text?: string | null;
        p_result_text?: string | null;
        p_denominator_text?: string | null;
        p_limitations?: string | null;
        p_not_improved?: string | null;
        p_validator_name?: string | null;
        p_validator_role?: string | null;
        p_client_name?: string | null;
        p_anonymized?: boolean;
        p_baseline_value?: number | null;
        p_result_value?: number | null;
        p_value_unit?: string | null;
        p_denominator_n?: number | null;
        p_client_org_ref?: string | null;
        p_measurement_study_ref?: string | null;
        p_id?: string | null;
      };
      returns: string;
    };
    "cases.submit_for_review": { args: { p_case_id: string }; returns: null };
    "cases.approve": { args: { p_case_id: string }; returns: null };
    "cases.publish": { args: { p_case_id: string }; returns: null };
    "cases.withdraw": { args: { p_case_id: string; p_reason: string }; returns: null };
    "cases.add_authorization": {
      args: {
        p_case_id: string;
        p_granted_by_name: string;
        p_granted_by_role: string;
        p_scope: string;
        p_allows_org_name?: boolean;
        p_allows_numbers?: boolean;
        p_allows_testimonial?: boolean;
        p_document_ref?: string | null;
        p_granted_at?: string | null;
      };
      returns: string;
    };
    "cases.revoke_authorization": { args: { p_authorization_id: string; p_reason: string }; returns: null };
    "cases.add_testimonial": { args: { p_case_id: string; p_text: string; p_author_role: string; p_author_name?: string | null }; returns: string };
    "cases.approve_testimonial": { args: { p_testimonial_id: string; p_evidence: string; p_approved_at?: string | null }; returns: null };
  }
}

/** Rótulos pt-BR das ações de auditoria do módulo (AuditTimeline). */
registerAuditLabels({
  "case.created": "Caso criado",
  "case.updated": "Ficha do caso alterada",
  "case.submitted": "Caso enviado para revisão",
  "case.approved": "Caso aprovado",
  "case.published": "Caso publicado",
  "case.withdrawn": "Caso retirado",
  "case.public_listed": "Vitrine consultada",
  "case.public_viewed": "Caso visto no site",
  "case_authorization.granted": "Autorização de publicação registrada",
  "case_authorization.revoked": "Autorização de publicação revogada",
  "testimonial.added": "Depoimento registrado",
  "testimonial.approved": "Depoimento aprovado pelo cliente",
});
